(() => {
  const WISHLIST_KEY = 'embe:wishlist:v1';

  function safeParse(json, fallback) {
    try {
      return JSON.parse(json);
    } catch {
      return fallback;
    }
  }

  function normalizeHandles(list) {
    const out = [];
    const seen = new Set();
    if (!Array.isArray(list)) return out;
    for (const item of list) {
      let h = '';
      if (typeof item === 'string') h = item.trim();
      else if (item && typeof item === 'object' && typeof item.handle === 'string') h = item.handle.trim();
      if (!h || seen.has(h)) continue;
      seen.add(h);
      out.push(h);
    }
    return out;
  }

  function getWishlist() {
    const raw = window.localStorage.getItem(WISHLIST_KEY);
    const arr = safeParse(raw, []);
    return normalizeHandles(Array.isArray(arr) ? arr : []);
  }

  function setWishlist(handles) {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    window.dispatchEvent(new CustomEvent('embe:wishlist:changed', { detail: { handles } }));
  }

  function toggleWishlist(handle) {
    const h = String(handle || '').trim();
    if (!h) return false;
    const list = getWishlist();
    const idx = list.indexOf(h);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.unshift(h);
    }
    setWishlist(list);
    return idx < 0;
  }

  function isWishlisted(handle) {
    const h = String(handle || '').trim();
    return h ? getWishlist().includes(h) : false;
  }

  function refreshWishlistButtons(root = document) {
    root.querySelectorAll('[data-card-action="wishlist"][data-product-handle]').forEach((btn) => {
      const handle = (btn.getAttribute('data-product-handle') || '').trim();
      const active = handle && isWishlisted(handle);
      btn.classList.toggle('is-active', Boolean(active));
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function startActionLoading(btn) {
    if (!btn || btn.classList.contains('loading')) return;
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('loading');
    const sp = btn.querySelector('.loading__spinner');
    if (sp) sp.classList.remove('hidden');
  }

  function stopActionLoading(btn) {
    if (!btn) return;
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('aria-busy');
    btn.classList.remove('loading');
    const sp = btn.querySelector('.loading__spinner');
    if (sp) sp.classList.add('hidden');
  }

  function findQuickAddTrigger(cardEl) {
    return (
      cardEl?.querySelector('[data-quick-add-trigger]') ||
      cardEl?.querySelector('modal-opener [data-product-url]') ||
      null
    );
  }

  function getQuickAddModal(cardEl) {
    const trigger = findQuickAddTrigger(cardEl);
    const opener = trigger?.closest('modal-opener');
    const sel = opener?.getAttribute('data-modal');
    return sel ? document.querySelector(sel) : null;
  }

  function waitForModalOpen(modalEl, timeoutMs = 20000) {
    if (!modalEl) {
      return Promise.reject(new Error('No quick-add modal'));
    }
    if (modalEl.hasAttribute('open')) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const obs = new MutationObserver(() => {
        if (modalEl.hasAttribute('open')) {
          obs.disconnect();
          clearTimeout(tid);
          resolve();
        }
      });
      obs.observe(modalEl, { attributes: true, attributeFilter: ['open'] });
      const tid = setTimeout(() => {
        obs.disconnect();
        reject(new Error('Quick add open timeout'));
      }, timeoutMs);
    });
  }

  async function openQuickAddWithFeedback(iconBtn, cardEl) {
    const trigger = findQuickAddTrigger(cardEl);
    if (!trigger) return false;

    const modal = getQuickAddModal(cardEl);
    startActionLoading(iconBtn);
    try {
      trigger.click();
      await waitForModalOpen(modal, 20000);
    } catch (e) {
      console.error(e);
    } finally {
      stopActionLoading(iconBtn);
    }
    return true;
  }

  async function addToCart(variantId) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';

    const config =
      typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (cart?.getSectionsToRender) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    const res = await fetch(cartAddUrl, config);
    const text = await res.text();

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      console.error(
        '[card-product-actions] Cart add returned non-JSON (status %s). First bytes: %s',
        res.status,
        text.slice(0, 200)
      );
      throw new Error('Cart add failed');
    }

    if (data?.status) throw new Error(data?.description || data?.message || 'Cart error');

    if (!cart?.renderContents) {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'card-product-actions',
        productVariantId: String(variantId),
        cartData: data,
      });
    }

    cart.renderContents(data);
  }

  function onClick(e) {
    const btn = e.target.closest('[data-card-action]');
    if (!btn) return;

    const action = btn.getAttribute('data-card-action');

    if (action === 'wishlist') {
      const handle = (btn.getAttribute('data-product-handle') || '').trim();
      if (!handle) return;
      toggleWishlist(handle);
      refreshWishlistButtons(document);
      return;
    }

    const card = btn.closest('[data-card-product]');
    if (!card) return;

    if (btn.classList.contains('loading')) return;

    if (action === 'quickview') {
      openQuickAddWithFeedback(btn, card);
      return;
    }

    if (action === 'addtocart') {
      const variantsCount = Number(btn.getAttribute('data-variants-count') || '0');
      const variantId = btn.getAttribute('data-variant-id');

      if (variantsCount > 1 || !variantId) {
        openQuickAddWithFeedback(btn, card);
        return;
      }

      startActionLoading(btn);
      addToCart(variantId)
        .catch((err) => console.error(err))
        .finally(() => {
          stopActionLoading(btn);
        });
    }
  }

  document.addEventListener('click', onClick);
  window.addEventListener('embe:wishlist:changed', () => refreshWishlistButtons(document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshWishlistButtons(document), { once: true });
  } else {
    refreshWishlistButtons(document);
  }
})();
